/**
 * The stage of a video meeting, laid out the way Google Meet does it:
 *
 *  - alone: your own picture fills the stage;
 *  - two people: the other person fills the stage, you sit small in the
 *    bottom-right corner;
 *  - three or more: the person talking fills the stage with the others in a
 *    strip, or — the "everyone" layout — a grid of all of them;
 *  - a shared screen (anyone's, yours included) takes the stage, the people
 *    shrink to the strip.
 *
 * Your own share IS shown to you, as in Meet: if you share this very window
 * you get the mirror-in-a-mirror picture, which is also the clearest proof
 * that it works.
 */
import { useEffect, useState } from 'react';
import type { VoiceScreenShare } from '../../contexts/VoiceRoomContext';
import type { MeetMember } from '../../hooks/useMeetRoom';
import { ScreenShareStage } from '../teamChat/ScreenShareStage';
import { VideoTile } from './VideoTile';

export type MeetLayout = 'speaker' | 'grid';

function gridColumns(count: number): string {
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-1 sm:grid-cols-2';
  if (count <= 4) return 'grid-cols-2';
  if (count <= 9) return 'grid-cols-2 md:grid-cols-3';
  return 'grid-cols-3 md:grid-cols-4';
}

function SelfCorner({ member, speaking }: { member: MeetMember; speaking: boolean }) {
  return (
    <div className="absolute bottom-3 right-3 z-10 aspect-video w-32 shadow-2xl sm:w-44 md:w-56">
      <VideoTile member={member} speaking={speaking} size="fill" />
    </div>
  );
}

function Strip({ members, speaking }: { members: MeetMember[]; speaking: Set<string> }) {
  if (members.length === 0) return null;
  return (
    <div className="flex h-20 shrink-0 gap-2 overflow-x-auto sm:h-24">
      {members.map((m) => (
        <VideoTile key={m.id} member={m} speaking={speaking.has(m.id)} size="compact" />
      ))}
    </div>
  );
}

export function VideoGrid({
  members,
  speakingIds,
  screenShares,
  layout,
}: {
  members: MeetMember[];
  speakingIds: string[];
  screenShares: VoiceScreenShare[];
  layout: MeetLayout;
}) {
  const [focusedShareId, setFocusedShareId] = useState<string | null>(null);
  const speaking = new Set(speakingIds);
  const me = members.find((m) => m.isLocal) ?? null;
  const others = members.filter((m) => !m.isLocal);

  // Whoever spoke last stays on the stage until someone else speaks — a stage
  // that flips on every pause is unwatchable.
  const [featuredId, setFeaturedId] = useState<string | null>(null);
  useEffect(() => {
    const talker = others.find((m) => speaking.has(m.id));
    if (talker) setFeaturedId(talker.id);
    // `others`/`speaking` are derived each render; the ids are what matter.
  }, [speakingIds, members]);
  const featured = others.find((m) => m.id === featuredId) ?? others[0] ?? null;

  // A shared screen wins over everything.
  if (screenShares.length > 0) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-2">
        <div className="min-h-0 flex-1">
          <ScreenShareStage
            shares={screenShares}
            focusedId={focusedShareId}
            onFocus={setFocusedShareId}
          />
        </div>
        <Strip members={members} speaking={speaking} />
      </div>
    );
  }

  // Alone: my own picture, full stage.
  if (others.length === 0) {
    return (
      <div className="h-full min-h-0">
        {me && <VideoTile member={me} speaking={speaking.has(me.id)} size="fill" />}
      </div>
    );
  }

  // Two people, or "speaker" layout with more: one big, me in the corner.
  if (others.length === 1 || layout === 'speaker') {
    const rest = others.filter((m) => m.id !== featured?.id);
    return (
      <div className="flex h-full min-h-0 flex-col gap-2">
        <div className="relative min-h-0 flex-1">
          {featured && (
            <VideoTile member={featured} speaking={speaking.has(featured.id)} size="fill" />
          )}
          {me && <SelfCorner member={me} speaking={speaking.has(me.id)} />}
        </div>
        <Strip members={rest} speaking={speaking} />
      </div>
    );
  }

  // Everyone on screen.
  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div
        className={`grid h-full ${gridColumns(members.length)} auto-rows-fr content-stretch gap-2`}
      >
        {members.map((m) => (
          <VideoTile
            key={m.id}
            member={m}
            speaking={speaking.has(m.id)}
            size="fill"
            className="min-h-32"
          />
        ))}
      </div>
    </div>
  );
}
